import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useCallback } from 'react';
import {
    Card, Tabs, Descriptions, Button, Space, Tag, Modal, Form, Input, Select,
    message, Row, Col, Statistic, Progress, Alert, Spin, Tooltip, Divider, List
} from 'antd';
import {
    SettingOutlined, GlobalOutlined, FolderOpenOutlined, AppstoreOutlined,
    ClockCircleOutlined, CloudServerOutlined, CodeOutlined, ReloadOutlined,
    PlayCircleOutlined, PauseCircleOutlined, ArrowLeftOutlined, ToolOutlined,
    DatabaseOutlined, HddOutlined, ApiOutlined, SafetyCertificateOutlined,
    ThunderboltOutlined, ExclamationCircleOutlined, CheckCircleOutlined,
    CodeSandboxOutlined, ConsoleSqlOutlined
} from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import hostService from '../../../shared/services/hostService';

// Tab Components - 嵌入式引用
import FileManager from './FileManager';
import CronManager from './CronManager';
import ServiceManager from './ServiceManager';
import AppManager from './AppManager';
import MySites from './MySites';
import HostTerminal from './components/HostTerminal';
import PhpConfigManager from './PhpConfigManager';

import { userPath } from '../../../shared/config/paths';

const { Option } = Select;

const HostDetail = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const [container, setContainer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [sitesCount, setSitesCount] = useState(0);
    const [runtimeVersions, setRuntimeVersions] = useState(null);

    // PHP Version Modal
    const [phpVersionModalVisible, setPhpVersionModalVisible] = useState(false);
    const [phpVersion, setPhpVersion] = useState('');
    const [versionSwitching, setVersionSwitching] = useState(false);

    // 加载容器信息
    const loadContainer = useCallback(async () => {
        try {
            const response = await hostService.getContainer(id);
            if (response.success) {
                setContainer(response.data);
            } else {
                message.error(response.error || t('Failed to retrieve container information'));
                navigate(userPath('/containers'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve container information'));
            navigate(userPath('/containers'));
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    // 加载统计信息
    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const response = await hostService.getContainerStats(id);
            if (response.success) {
                setStats(response.data);
            }
        } catch (error) {
            console.error('获取统计信息失败:', error);
        } finally {
            setStatsLoading(false);
        }
    }, [id]);

    // 加载站点数量
    const loadSitesCount = useCallback(async () => {
        try {
            const response = await hostService.getSites(id);
            if (response.success) {
                setSitesCount((response.data || []).length);
            }
        } catch (error) {
            console.error('获取站点数量失败:', error);
        }
    }, [id]);

    // 加载运行时版本信息
    const loadRuntimeVersions = useCallback(async () => {
        try {
            const response = await hostService.getRuntimeVersions(id);
            if (response.success) {
                setRuntimeVersions(response.data);
            }
        } catch (error) {
            console.error('获取运行时版本失败:', error);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            loadContainer();
            loadStats();
            loadSitesCount();
            loadRuntimeVersions();
        }
    }, [id, loadContainer, loadStats, loadSitesCount, loadRuntimeVersions]);

    // Tab切换时加载相关数据
    useEffect(() => {
        if (activeTab === 'sites') {
            loadSitesCount();
        }
    }, [activeTab, loadSitesCount]);

    // 容器操作
    const handleStart = async () => {
        try {
            const response = await hostService.startContainer(id);
            if (response.success) {
                message.success(t('Container started'));
                loadContainer();
                loadStats();
            } else {
                message.error(response.error || t('Failed to start'));
            }
        } catch (error) {
            message.error(t('Failed to start'));
        }
    };

    const handleStop = async () => {
        Modal.confirm({
            title: t('Stop the container?'),
            icon: <ExclamationCircleOutlined />,
            content: t('Stopping the container will make all services inside it unavailable, including websites and databases.'),
            okText: t('Confirm stop'),
            okType: 'danger',
            cancelText: t('Cancel'),
            onOk: async () => {
                try {
                    const response = await hostService.stopContainer(id);
                    if (response.success) {
                        message.success(t('Container stopped'));
                        loadContainer();
                        loadStats();
                    } else {
                        message.error(response.error || t('Failed to stop'));
                    }
                } catch (error) {
                    message.error(t('Failed to stop'));
                }
            }
        });
    };

    const handleRestart = async () => {
        try {
            message.loading({ content: t('Restarting container...'), key: 'restart' });
            const response = await hostService.restartContainer(id);
            if (response.success) {
                message.success({ content: t('Container restarted'), key: 'restart' });
                loadContainer();
                loadStats();
            } else {
                message.error({ content: response.error || t('Restart failed'), key: 'restart' });
            }
        } catch (error) {
            message.error({ content: t('Restart failed'), key: 'restart' });
        }
    };

    const handleFixPermissions = async () => {
        try {
            message.loading({ content: t('Repairing permissions...'), key: 'fix' });
            const response = await hostService.fixPermissions(id);
            if (response.success) {
                message.success({ content: t('Permissions repaired'), key: 'fix' });
            } else {
                message.error({ content: response.error || t('Failed to repair permissions'), key: 'fix' });
            }
        } catch (error) {
            message.error({ content: t('Failed to repair permissions'), key: 'fix' });
        }
    };

    const handleNginxReload = async () => {
        try {
            message.loading({ content: t('Reloading Nginx...'), key: 'nginx' });
            const response = await hostService.nginxReload(id);
            if (response.success) {
                message.success({ content: t('Nginx reloaded'), key: 'nginx' });
            } else {
                message.error({ content: response.error || t('Failed to reload Nginx'), key: 'nginx' });
            }
        } catch (error) {
            message.error({ content: t('Failed to reload Nginx'), key: 'nginx' });
        }
    };

    const handleNginxTest = async () => {
        try {
            const response = await hostService.nginxTest(id);
            if (response.success) {
                message.success(t('Nginx configuration test passed'));
            } else {
                message.error(response.error || t('Nginx configuration test failed'));
            }
        } catch (error) {
            message.error(t('Nginx configuration test failed'));
        }
    };

    const handleSwitchPhpVersion = async () => {
        if (!phpVersion) {
            message.warning(t('Select a PHP version'));
            return;
        }
        setVersionSwitching(true);
        try {
            const response = await hostService.switchPhpVersion(id, phpVersion);
            if (response.success) {
                message.success(t('PHP version switched'));
                setPhpVersionModalVisible(false);
                loadContainer();
            } else {
                message.error(response.error || t('Failed to switch PHP version'));
            }
        } catch (error) {
            message.error(t('Failed to switch PHP version'));
        } finally {
            setVersionSwitching(false);
        }
    };

    // 状态显示
    const getStatusTag = (status) => {
        const statusMap = {
            running: { color: 'success', text: t('Running'), icon: <CheckCircleOutlined /> },
            stopped: { color: 'default', text: t('Stopped'), icon: <ExclamationCircleOutlined /> },
            created: { color: 'processing', text: t('Created'), icon: <ClockCircleOutlined /> },
            paused: { color: 'warning', text: t('Paused'), icon: <ExclamationCircleOutlined /> },
            restarting: { color: 'processing', text: t('Restarting'), icon: <ReloadOutlined spin /> },
            exited: { color: 'error', text: t('Signed out'), icon: <ExclamationCircleOutlined /> },
        };
        const config = statusMap[status] || { color: 'default', text: status, icon: null };
        return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
    };

    const getProgressStatus = (percent) => {
        if (percent >= 90) return 'exception';
        if (percent >= 70) return 'normal';
        return 'success';
    };

    // 加载中状态
    if (loading) {
        return (
            <Card>
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin size="large" tip={t('Loading...')} />
                </div>
            </Card>
        );
    }

    if (!container) {
        return (
            <Card>
                <Alert
                    message={t('Container does not exist')}
                    description={t('The specified container could not be found. It may have been deleted.')}
                    type="error"
                    showIcon
                    action={
                        <Button onClick={() => navigate(userPath('/containers'))}>
                            {t('Back to list')}
                        </Button>
                    }
                />
            </Card>
        );
    }

    // 计算资源使用百分比
    const cpuPercent = stats?.cpu_percent || 0;
    const memoryPercent = stats?.memory_percent || 0;
    const diskPercent = stats?.disk_percent || 0;

    // Tab配置
    const tabItems = [
        {
            key: 'overview',
            label: <span><SettingOutlined /> {t('Overview')}</span>,
            children: (
                <div>
                    {/* 资源使用统计卡片 */}
                    <Row gutter={16} style={{ marginBottom: 24 }}>
                        <Col xs={24} sm={12} lg={6}>
                            <Card hoverable>
                                <Statistic
                                    title={t('CPU usage')}
                                    value={cpuPercent}
                                    precision={1}
                                    suffix="%"
                                    prefix={<ThunderboltOutlined />}
                                    valueStyle={{ color: cpuPercent >= 90 ? '#cf1322' : cpuPercent >= 70 ? '#faad14' : '#3f8600' }}
                                    loading={statsLoading}
                                />
                                <Progress
                                    percent={cpuPercent}
                                    status={getProgressStatus(cpuPercent)}
                                    showInfo={false}
                                    strokeColor={cpuPercent >= 90 ? '#cf1322' : cpuPercent >= 70 ? '#faad14' : '#52c41a'}
                                />
                                <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                    {t('Limit: {{limit}}', { limit: container.cpu_limit || t('Unlimited') })}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card hoverable>
                                <Statistic
                                    title={t('Memory usage')}
                                    value={stats?.memory_usage || 0}
                                    suffix="MB"
                                    prefix={<DatabaseOutlined />}
                                    valueStyle={{ color: memoryPercent >= 90 ? '#cf1322' : memoryPercent >= 70 ? '#faad14' : '#3f8600' }}
                                    loading={statsLoading}
                                />
                                <Progress
                                    percent={memoryPercent}
                                    status={getProgressStatus(memoryPercent)}
                                    showInfo={false}
                                    strokeColor={memoryPercent >= 90 ? '#cf1322' : memoryPercent >= 70 ? '#faad14' : '#52c41a'}
                                />
                                <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                    {t('Limit: {{limit}}', { limit: container.memory_limit || t('Unlimited') })}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card hoverable>
                                <Statistic
                                    title={t('Disk usage')}
                                    value={stats?.disk_usage || 0}
                                    precision={2}
                                    suffix="GB"
                                    prefix={<HddOutlined />}
                                    valueStyle={{ color: diskPercent >= 90 ? '#cf1322' : diskPercent >= 70 ? '#faad14' : '#3f8600' }}
                                    loading={statsLoading}
                                />
                                <Progress
                                    percent={diskPercent}
                                    status={getProgressStatus(diskPercent)}
                                    showInfo={false}
                                    strokeColor={diskPercent >= 90 ? '#cf1322' : diskPercent >= 70 ? '#faad14' : '#52c41a'}
                                />
                                <div style={{ marginTop: 8, fontSize: '12px', color: '#999' }}>
                                    {t('Limit: {{limit}}', { limit: container.disk_limit || t('Unlimited') })}
                                </div>
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} lg={6}>
                            <Card hoverable>
                                <Statistic
                                    title={t('Site count')}
                                    value={sitesCount}
                                    prefix={<GlobalOutlined />}
                                    suffix={t('items')}
                                />
                                <div style={{ marginTop: 24, fontSize: '12px', color: '#999' }}>
                                    {t('Network traffic: {{rx}}MB in / {{tx}}MB out', { rx: stats?.network_rx || 0, tx: stats?.network_tx || 0 })}
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* 快捷操作 */}
                    <Card title={t('Quick actions')} style={{ marginBottom: 24 }}>
                        <Space wrap size="middle">
                            {container.status === 'running' ? (
                                <>
                                    <Button icon={<PauseCircleOutlined />} onClick={handleStop}>
                                        {t('Stop container')}
                                    </Button>
                                    <Button icon={<ReloadOutlined />} onClick={handleRestart}>
                                        {t('Restart container')}
                                    </Button>
                                </>
                            ) : (
                                <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleStart}>
                                    {t('Start container')}
                                </Button>
                            )}
                            <Divider type="vertical" />
                            <Tooltip title={t('Fix /var/www/html directory permissions')}>
                                <Button icon={<ToolOutlined />} onClick={handleFixPermissions}>
                                    {t('Fix permissions')}
                                </Button>
                            </Tooltip>
                            <Tooltip title={t('Test Nginx configuration syntax')}>
                                <Button onClick={handleNginxTest}>{t('Test Nginx')}</Button>
                            </Tooltip>
                            <Tooltip title={t('Reload Nginx configuration')}>
                                <Button onClick={handleNginxReload}>{t('Reload Nginx')}</Button>
                            </Tooltip>
                            <Divider type="vertical" />
                            <Button onClick={() => {
                                setPhpVersion(container.php_version || '8.2');
                                setPhpVersionModalVisible(true);
                            }}>
                                {t('Switch PHP version')}
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={() => { loadContainer(); loadStats(); }}>
                                {t('Refresh')}
                            </Button>
                        </Space>
                    </Card>

                    {/* 基本信息 */}
                    <Card title={t('Basic information')}>
                        <Descriptions bordered column={{ xxl: 3, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}>
                            <Descriptions.Item label={t('Container name')}>{container.name}</Descriptions.Item>
                            <Descriptions.Item label={t('Container ID')}>
                                <Tooltip title={container.container_id}>
                                    <code>{container.container_id?.substring(0, 12) || '-'}...</code>
                                </Tooltip>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Status')}>{getStatusTag(container.status)}</Descriptions.Item>
                            <Descriptions.Item label={t('PHP version')}>
                                <Tag color="blue">PHP {container.php_version || '8.2'}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Node.js version')}>
                                <Tag color="green">Node {container.node_version || '20'}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Python version')}>
                                <Tag color="orange">Python {container.python_version || '3.11'}</Tag>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('CPU limit')}>{container.cpu_limit || t('Unlimited')}</Descriptions.Item>
                            <Descriptions.Item label={t('Memory limit')}>{container.memory_limit || t('Unlimited')}</Descriptions.Item>
                            <Descriptions.Item label={t('Disk limit')}>{container.disk_limit || t('Unlimited')}</Descriptions.Item>
                            <Descriptions.Item label={t('Working directory')} span={3}>
                                <code>{container.work_dir || '/var/www/html'}</code>
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Created at')}>
                                {container.created_at ? new Date(container.created_at).toLocaleString('zh-CN') : '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label={t('Updated at')}>
                                {container.updated_at ? new Date(container.updated_at).toLocaleString('zh-CN') : '-'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </div>
            )
        },
        {
            key: 'sites',
            label: <span><GlobalOutlined /> {t('Site management')}</span>,
            children: <MySites containerId={parseInt(id)} embedded={true} />
        },
        {
            key: 'files',
            label: <span><FolderOpenOutlined /> {t('File manager')}</span>,
            children: <FileManager containerId={parseInt(id)} embedded={true} />
        },
        {
            key: 'apps',
            label: <span><AppstoreOutlined /> {t('App management')}</span>,
            children: <AppManager containerId={parseInt(id)} embedded={true} />
        },
        {
            key: 'services',
            label: <span><CloudServerOutlined /> {t('Background services')}</span>,
            children: <ServiceManager containerId={parseInt(id)} embedded={true} />
        },
        {
            key: 'cron',
            label: <span><ClockCircleOutlined /> {t('Scheduled tasks')}</span>,
            children: <CronManager containerId={parseInt(id)} embedded={true} />
        },
        {
            key: 'config',
            label: <span><CodeOutlined /> {t('Configuration management')}</span>,
            children: (
                <div>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} lg={12}>
                            <Card title={<><CodeOutlined /> {t('PHP configuration')}</>} style={{ height: '100%' }}>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label={t('Current version')}>
                                        <Tag color="blue">PHP {container.php_version || '8.2'}</Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('Available versions')}>
                                        {runtimeVersions?.php?.available?.join(', ') || 'PHP 8.1, 8.2, 8.3'}
                                    </Descriptions.Item>
                                </Descriptions>
                                <Divider />
                                <Space>
                                    <Button type="primary" onClick={() => {
                                        setPhpVersion(container.php_version || '8.2');
                                        setPhpVersionModalVisible(true);
                                    }}>
                                        {t('Switch PHP version')}
                                    </Button>
                                </Space>
                                <Alert
                                    message={t('Switching PHP versions affects all sites using the default PHP')}
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card title={<><ApiOutlined /> {t('Nginx configuration')}</>} style={{ height: '100%' }}>
                                <Alert
                                    message={t('The site\'s Nginx configuration is managed automatically by the system')}
                                    description={t('Each site\'s Nginx configuration is generated automatically from its settings. You can update these settings in site management.')}
                                    type="info"
                                    showIcon
                                    style={{ marginBottom: 16 }}
                                />
                                <Space>
                                    <Button onClick={handleNginxTest}>{t('Test configuration')}</Button>
                                    <Button type="primary" onClick={handleNginxReload}>{t('Reload configuration')}</Button>
                                </Space>
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card title={<><ToolOutlined /> {t('Operations tools')}</>}>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Button icon={<ToolOutlined />} onClick={handleFixPermissions} block>
                                        {t('Fix file permissions')}
                                    </Button>
                                    <Button icon={<ReloadOutlined />} onClick={handleRestart} block>
                                        {t('Restart container')}
                                    </Button>
                                </Space>
                                <Alert
                                    message={t('Permission repair instructions')}
                                    description={t('Restore /var/www/html ownership to hostmanager:hostmanager to fix file permission issues.')}
                                    type="info"
                                    showIcon
                                    style={{ marginTop: 16 }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} lg={12}>
                            <Card title={<><SafetyCertificateOutlined /> {t('SSL certificates')}</>}>
                                <Alert
                                    message={t('SSL certificate management')}
                                    description={t('SSL certificates are configured in site management. Open the site management page and edit the relevant site to configure its SSL certificate.')}
                                    type="info"
                                    showIcon
                                />
                                <Button
                                    type="link"
                                    onClick={() => setActiveTab('sites')}
                                    style={{ marginTop: 8, padding: 0 }}
                                >
                                    {t('Go to site management →')}
                                </Button>
                            </Card>
                        </Col>
                    </Row>

                    {/* 运行时版本信息 */}
                    {runtimeVersions && (
                        <Card title={<><CodeSandboxOutlined /> {t('Runtime environment')}</>} style={{ marginTop: 16 }}>
                            <Row gutter={16}>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="PHP"
                                        value={runtimeVersions.php?.current || container.php_version || '8.2'}
                                        valueStyle={{ fontSize: 16 }}
                                    />
                                    <div style={{ fontSize: 12, color: '#999' }}>
                                        {t('Available: {{versions}}', { versions: runtimeVersions.php?.available?.join(', ') || '-' })}
                                    </div>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Node.js"
                                        value={runtimeVersions.nodejs?.current || container.node_version || '20'}
                                        valueStyle={{ fontSize: 16 }}
                                    />
                                    <div style={{ fontSize: 12, color: '#999' }}>
                                        {t('Available: {{versions}}', { versions: runtimeVersions.nodejs?.available?.join(', ') || '-' })}
                                    </div>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Python"
                                        value={runtimeVersions.python?.current || container.python_version || '3.11'}
                                        valueStyle={{ fontSize: 16 }}
                                    />
                                    <div style={{ fontSize: 12, color: '#999' }}>
                                        {t('Available: {{versions}}', { versions: runtimeVersions.python?.available?.join(', ') || '-' })}
                                    </div>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <Statistic
                                        title="Golang"
                                        value={runtimeVersions.golang?.current || '-'}
                                        valueStyle={{ fontSize: 16 }}
                                    />
                                    <div style={{ fontSize: 12, color: '#999' }}>
                                        {t('Available: {{versions}}', { versions: runtimeVersions.golang?.available?.join(', ') || '-' })}
                                    </div>
                                </Col>
                                <Col xs={12} sm={6} style={{ marginTop: 16 }}>
                                    <Statistic
                                        title="Java"
                                        value={runtimeVersions.java?.current || '-'}
                                        valueStyle={{ fontSize: 16 }}
                                    />
                                    <div style={{ fontSize: 12, color: '#999' }}>
                                        {t('Available: {{versions}}', { versions: runtimeVersions.java?.available?.join(', ') || '-' })}
                                    </div>
                                </Col>
                                <Col xs={12} sm={6} style={{ marginTop: 16 }}>
                                    <Statistic
                                        title="Ruby"
                                        value={runtimeVersions.ruby?.current || '-'}
                                        valueStyle={{ fontSize: 16 }}
                                    />
                                    <div style={{ fontSize: 12, color: '#999' }}>
                                        {t('Available: {{versions}}', { versions: runtimeVersions.ruby?.available?.join(', ') || '-' })}
                                    </div>
                                </Col>
                            </Row>
                            <Alert
                                style={{ marginTop: 16 }}
                                type="info"
                                showIcon
                                message={t('About switching versions')}
                                description={t('PHP supports version switching (8.1/8.2). Alpine limits Node/Python/Go/Java/Ruby to one version per image. If you need another version, please specify it in a support ticket.')}
                            />
                        </Card>
                    )}
                </div>
            )
        },
        {
            key: 'phpconfig',
            label: <span><CodeOutlined /> {t('PHP configuration')}</span>,
            children: <PhpConfigManager containerId={parseInt(id)} />
        },
        {
            key: 'terminal',
            label: <span><ConsoleSqlOutlined /> {t('Web terminal')}</span>,
            children: (
                <Card title={t('Web terminal')} bodyStyle={{ padding: 0 }}>
                    <Alert
                        message={t('Instructions')}
                        description={t('The web terminal provides direct access to the container\'s command line. Please use it carefully, as improper operations may disrupt the service.')}
                        type="warning"
                        showIcon
                        style={{ margin: 16, marginBottom: 0 }}
                    />
                    <HostTerminal containerId={parseInt(id)} />
                </Card>
            )
        }
    ];

    return (
        <div>
            {/* 页面头部 */}
            <div style={{
                marginBottom: 16,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 16
            }}>
                <Space align="center">
                    <Button
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(userPath('/containers'))}
                    >
                        {t('Back to list')}
                    </Button>
                    <h2 style={{ margin: 0 }}>
                        {container.name}
                    </h2>
                    {getStatusTag(container.status)}
                </Space>
                <Space>
                    {container.status === 'running' ? (
                        <>
                            <Button icon={<PauseCircleOutlined />} onClick={handleStop}>{t('Stop')}</Button>
                            <Button icon={<ReloadOutlined />} onClick={handleRestart}>{t('Restart')}</Button>
                        </>
                    ) : (
                        <Button type="primary" icon={<PlayCircleOutlined />} onClick={handleStart}>{t('Start')}</Button>
                    )}
                </Space>
            </div>

            {/* Tab页签 */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                type="card"
                size="large"
            />

            {/* PHP版本切换Modal */}
            <Modal
                title={t('Switch PHP version')}
                open={phpVersionModalVisible}
                onCancel={() => setPhpVersionModalVisible(false)}
                onOk={handleSwitchPhpVersion}
                confirmLoading={versionSwitching}
                okText={t('Confirm switch')}
            >
                <Alert
                    message={t('Notes')}
                    description={
                        <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
                            <li>{t('Switching PHP versions affects all sites using the default PHP version')}</li>
                            <li>{t('PHP-FPM restarts automatically after switching')}</li>
                            <li>{t('Please ensure your code is compatible with the target PHP version')}</li>
                        </ul>
                    }
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
                <Form.Item label={t('Select a PHP version')} style={{ marginBottom: 0 }}>
                    <Select
                        value={phpVersion}
                        onChange={setPhpVersion}
                        style={{ width: '100%' }}
                        size="large"
                    >
                        <Option value="8.1">
                            <Space>
                                <Tag color="blue">PHP 8.1</Tag>
                                <span>{t('LTS Long-Term Support release')}</span>
                            </Space>
                        </Option>
                        <Option value="8.2">
                            <Space>
                                <Tag color="green">PHP 8.2</Tag>
                                <span>{t('Recommended version')}</span>
                            </Space>
                        </Option>
                    </Select>
                </Form.Item>
            </Modal>
        </div>
    );
};

export default HostDetail;
